import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useRole } from "../../components/AppShell";
import { BackIcon, WhatsAppIcon } from "../../components/icons";
import { Avatar, ListSkeleton, useConfirm, useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { formatPhone, shortTime } from "../../lib/format";
import {
  useAddPartRequestNote,
  useCase,
  useCaseStatuses,
  useDeletePartRequest,
  useMe,
  usePartRequest,
  usePartRequestStatuses,
  useTeam,
  useUpdateCase,
  useUpdatePartRequest,
} from "../../lib/queries";
import type { PartRequestCard } from "../../lib/types";
import { HistoryCard, NoticeCard } from "./CasePage";
import { caseNumber, fillPartNotice, lastStatusAt, partRequestNumber } from "./case-text";
import { type PartRequestDraft, PartRequestFields } from "./PartRequestDialog";

export function PartRequestPage() {
  const { requestId } = useParams({ from: "/app/postventa/repuestos/$requestId" });
  const r = usePartRequest(requestId);
  if (r.isError)
    return (
      <div className="page">
        <BackLink />
        <p className="muted">Esta solicitud ya no existe.</p>
      </div>
    );
  if (!r.data)
    return (
      <div className="page">
        <BackLink />
        <div className="card">
          <ListSkeleton rows={3} />
        </div>
      </div>
    );
  return <RequestView key={r.data.id} r={r.data} />;
}

function BackLink() {
  return (
    <Link className="back-link" to="/postventa" search={{ tab: "repuestos" }}>
      <BackIcon />
      Repuestos
    </Link>
  );
}

function RequestView({ r }: { r: PartRequestCard }) {
  const role = useRole();
  const me = useMe().data;
  const statuses = usePartRequestStatuses().data ?? [];
  const team = useTeam().data?.items ?? [];
  const names = new Map(team.map((m) => [m.id, m.name]));
  const update = useUpdatePartRequest(r.id);
  const add = useAddPartRequestNote(r.id);
  const remove = useDeletePartRequest();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<string | null>(null);

  async function changeStatus(statusId: string) {
    try {
      const saved = await update.mutateAsync({ statusId });
      const text = statuses.find((s) => s.id === statusId)?.notice;
      setNotice(text ? fillPartNotice(text, saved) : null);
    } catch {
      toast("No se pudo cambiar el estado.");
    }
  }

  async function changeAssignee(assigneeId: string) {
    try {
      await update.mutateAsync({ assigneeId: assigneeId || null });
    } catch (err) {
      toast(
        err instanceof ApiError && err.status === 403
          ? "Solo puedes tomar la solicitud para ti."
          : "No se pudo asignar.",
      );
    }
  }

  async function onDelete() {
    const ok = await confirm({
      title: `¿Eliminar la solicitud ${partRequestNumber(r.number)}?`,
      message:
        "Se borra con su historial y no se puede deshacer. Para terminarla, mejor pásala a un estado que la cierre.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await remove.mutateAsync(r.id);
    toast("Solicitud eliminada");
    navigate({ to: "/postventa", search: { tab: "repuestos" } });
  }

  const manager = role === "owner" || role === "admin";
  const assignable = manager ? team : team.filter((m) => m.id === me?.user.id || m.id === r.assigneeId);
  const forWhat = r.product ? `${r.product.model ? `${r.product.model} · ` : ""}${r.product.name}` : r.model;

  return (
    <div className="page">
      <BackLink />
      <div className="card case-hero">
        <div className="case-hero-main">
          <span className="case-number mono">
            {partRequestNumber(r.number)}
            {r.reference && <span className="faint"> · {r.reference}</span>}
          </span>
          <h1>
            {r.quantity > 1 ? `${r.quantity} × ` : ""}
            {r.partName}
          </h1>
          <span className="contact-hero-tags">
            <span className={`pill ${r.status.closed ? "pill-closed" : r.status.arrived ? "pill-bot" : "pill-human"}`}>
              {r.status.label}
            </span>
            {r.part?.model && <span className="tag mono">{r.part.model}</span>}
            {forWhat && <span className="tag">Para {forWhat}</span>}
            <span className="faint">Registrada {shortTime(r.createdAt)}</span>
          </span>
        </div>
        <Link className="case-customer" to="/contactos/$contactId" params={{ contactId: r.contact.id }}>
          <Avatar name={r.contact.name} fallback={r.contact.waId} />
          <span className="entity-main">
            <span className="entity-name">{r.contact.name ?? "Sin nombre"}</span>
            <span className="entity-meta mono">{formatPhone(r.contact.waId)}</span>
          </span>
        </Link>
        <div className="case-controls">
          <div className="field">
            <label htmlFor="part-status">Estado</label>
            <select
              className="select"
              id="part-status"
              value={r.statusId}
              disabled={update.isPending}
              onChange={(e) => changeStatus(e.target.value)}
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.closed ? `${s.label} (cierra)` : s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="part-assignee">Responsable</label>
            <select
              className="select"
              id="part-assignee"
              value={r.assigneeId ?? ""}
              disabled={update.isPending}
              onChange={(e) => changeAssignee(e.target.value)}
            >
              <option value="">Sin responsable</option>
              {assignable.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id === me?.user.id ? `${m.name} (yo)` : m.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {notice !== null && (
        <NoticeCard
          contactId={r.contactId}
          conversationId={r.conversationId}
          text={notice}
          onChange={setNotice}
          onDone={() => setNotice(null)}
        />
      )}

      {r.status.arrived && r.case && <MoveCaseCard caseId={r.case.id} arrivedAt={lastStatusAt(r.events)} />}

      <div className="contact-layout">
        <div className="case-side">
          <DetailsCard r={r} />
        </div>
        <HistoryCard events={r.events} names={names} onAdd={add.mutateAsync} pending={add.isPending} what="part" />
      </div>

      {manager && (
        <button className="btn btn-danger case-delete" type="button" onClick={onDelete}>
          Eliminar solicitud
        </button>
      )}
    </div>
  );
}

/**
 * The part arrived and a case waits for it: offers moving the case to its next status,
 * from here, without opening it. Nothing moves on its own.
 */
function MoveCaseCard({ caseId, arrivedAt }: { caseId: string; arrivedAt: number }) {
  const c = useCase(caseId).data;
  const statuses = useCaseStatuses().data ?? [];
  const update = useUpdateCase(caseId);
  const toast = useToast();
  const [done, setDone] = useState(false);
  // Once the case moved after the part arrived, it is not offered again.
  if (!c || c.status.closed || done || lastStatusAt(c.events) > arrivedAt) return null;
  const at = statuses.findIndex((s) => s.id === c.statusId);
  const next = at >= 0 ? statuses[at + 1] : undefined;
  // Arriving parts move a repair along; closing the case stays the advisor's call.
  if (!next || next.closed) return null;
  const target = next;

  async function move() {
    try {
      await update.mutateAsync({ statusId: target.id });
      toast(`Caso ${caseNumber(c?.number ?? 0)} pasado a ${target.label}`);
      setDone(true);
    } catch {
      toast("No se pudo cambiar el estado del caso.");
    }
  }

  return (
    <section className="card case-notice" aria-labelledby="part-case-title">
      <div className="card-body">
        <div>
          <h3 id="part-case-title">¿Avanzar el caso?</h3>
          <p className="muted">
            El caso{" "}
            <Link to="/postventa/$caseId" params={{ caseId }}>
              {caseNumber(c.number)}
            </Link>{" "}
            espera este repuesto y sigue en {c.status.label}. ¿Lo pasas a {target.label}?
          </p>
        </div>
        <div className="row">
          <button className="btn" type="button" onClick={() => setDone(true)}>
            Ahora no
          </button>
          <button className="btn btn-primary" type="button" disabled={update.isPending} onClick={move}>
            Pasar a {target.label}
          </button>
        </div>
      </div>
    </section>
  );
}

const draftOf = (r: PartRequestCard): PartRequestDraft => ({
  partId: r.partId,
  partName: r.partName,
  productId: r.productId,
  model: r.model,
  quantity: r.quantity,
  description: r.description,
  reference: r.reference,
});

function DetailsCard({ r }: { r: PartRequestCard }) {
  const update = useUpdatePartRequest(r.id);
  const toast = useToast();
  const [d, setD] = useState<PartRequestDraft>(() => draftOf(r));
  const dirty = JSON.stringify(d) !== JSON.stringify(draftOf(r));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await update.mutateAsync({ ...d, partName: d.partName.trim() });
      toast("Solicitud guardada");
    } catch {
      toast("No se pudo guardar.");
    }
  }

  return (
    <form className="card case-form" onSubmit={onSubmit}>
      <div className="card-body">
        <h3>Datos de la solicitud</h3>
        <PartRequestFields value={d} onChange={setD} idPrefix="part" />
        {r.case && (
          <Link className="conv-link-inline" to="/postventa/$caseId" params={{ caseId: r.case.id }}>
            Caso {caseNumber(r.case.number)}: {r.case.title}
          </Link>
        )}
        {r.conversationId && (
          <Link className="conv-link-inline" to="/bandeja" search={{ c: r.conversationId, f: "all" }}>
            <WhatsAppIcon />
            Ver la conversación donde se pidió
          </Link>
        )}
        <div className="row">
          <button className="btn" type="button" disabled={!dirty} onClick={() => setD(draftOf(r))}>
            Descartar
          </button>
          <button className="btn btn-primary" type="submit" disabled={!dirty || update.isPending || !d.partName.trim()}>
            {update.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </form>
  );
}
